import { useCallback, useEffect, useRef } from "react";

import type { Id } from "../api/types";
import { getLiveClient } from "./LiveProvider";

/** Signals closer together than this are one signal. */
const MIN_GAP_MS = 30_000;

/**
 * While an edit form is in use, tell the server so the lock on the element stays (2.10, architecture
 * 12.3). One signal when the form opens, then one for the use of the form: call `ping()` when the person
 * types or changes something. A form left open and untouched sends nothing, so the lock can run out.
 */
export function useStillEditing(elementId: Id | null | undefined, options: { checkEveryMs?: number } = {}): { ping(): void } {
  const checkEveryMs = options.checkEveryMs ?? 60_000;
  const lastSent = useRef(0);
  const touched = useRef(false);

  const send = useCallback(() => {
    if (!elementId) return;
    getLiveClient()?.sendStillEditing(elementId);
    lastSent.current = Date.now();
    touched.current = false;
  }, [elementId]);

  useEffect(() => {
    if (!elementId) return;
    send();
    const timer = setInterval(() => {
      if (touched.current) send();
    }, checkEveryMs);
    return () => clearInterval(timer);
  }, [elementId, send, checkEveryMs]);

  const ping = useCallback(() => {
    touched.current = true;
    if (Date.now() - lastSent.current >= MIN_GAP_MS) send();
  }, [send]);

  return { ping };
}
